import { openSocket, request as routeRequest, type MessageSocket } from "@wildlark/relay-client"
import type { ZodTypeAny } from "zod"
import { currentPlatformKind, nestClientName, nestClientPlatform } from "../platform/detect"
import { WILDLARK_TOOL_PREFIX } from "../product"
import { ClientToolRegistry, type ClientToolCallback, type ClientToolDefinition } from "./clientTools"
import type {
    ClientToolCancelNotification,
    ClientToolCallNotification,
    ClientToolResultParams,
    NestErrorCode,
    NestKnownErrorCode,
    NestMethod,
    NestMethodParams,
    NestMethodRequiresParams,
    NestMethodResult,
    NestNotificationMethod,
    NestNotificationParams,
    PermissionRequestNotification,
    PermissionRespondParams,
    SubscriptionFilter,
} from "./generated"
import { NEST_ERROR_CODES, NEST_NOTIFICATION_METHODS } from "./generated"
import { classifyNestConnectionFailure, type NestConnectionFailure } from "./connectionFailure"
import { blindbindRoute, nestRouteKey, nestRouteLabel, type NestConnection, type NestRoute } from "./routes"
import { type NestClientStatus, isNestRpcTraceEnabled, recordNestRpcLog, recordNestRpcMethodDuration, safeRpcLogContext, socketStateName } from "./rpcLog"
export type {
    NestErrorCode,
    NestMethod,
    NestMethodParams,
    NestMethodRequiresParams,
    NestMethodResult,
    NestNotificationMethod,
    NestNotificationParams,
    PermissionRequestNotification,
} from "./generated"
export type { NestClientStatus, NestRpcLogEvent, NestRpcLogPhase } from "./rpcLog"
export { clearNestRpcLog, getNestRpcLog, installNestRpcLogDebugApi } from "./rpcLog"

export type { NestConnection, NestRoute } from "./routes"

export interface NestRequestOptions {
    signal?: AbortSignal
    // Defaults: 60s request timeout and 150ms slow-RPC logging threshold.
    timeoutMs?: number
    slowMs?: number
}

export interface NestConnectOptions {
    timeoutMs?: number
}

export interface NestClientDiagnostics {
    status: NestClientStatus | "idle"
    nodeId: string | null
    lastCursor: string | null
    suspended: boolean
}

export type NestSubscriptionFilter = SubscriptionFilter

export interface NestNotification<M extends NestNotificationMethod = NestNotificationMethod> {
    method: M
    params?: NestNotificationParams<M>
    cursor?: string
}

type NestRequestId = number
type NestRequestMethodArgs<M extends NestMethod> =
    NestMethodRequiresParams<M> extends true
        ? [params: NestMethodParams<M>, options?: NestRequestOptions]
        : [params?: NestMethodParams<M>, options?: NestRequestOptions]
type NestNotificationHandler<M extends NestNotificationMethod = NestNotificationMethod> = (
    params: NestNotificationParams<M> | undefined,
    notification: NestNotification<M>
) => void
type NestStatusHandler = (status: NestClientStatus) => void
type NestConnectionFailureHandler = (failure: NestConnectionFailure) => void

export type PermissionPromptResponse = Pick<PermissionRespondParams, "decision" | "message">
export type PermissionPromptHandler = (request: PermissionRequestNotification) => Promise<PermissionPromptResponse>

interface NestResponse {
    id: NestRequestId
    result?: unknown
    error?: {
        code: string
        message: string
        data?: unknown
    }
}

interface PendingRequest {
    method: string
    params: unknown
    startedAt: number
    slowMs: number
    resolve(value: unknown): void
    reject(error: Error): void
    timeout: ReturnType<typeof setTimeout>
    abort?: () => void
    signal?: AbortSignal
}

interface RpcResponseSize {
    responseChars: number
    responseSize: string
}

export class NestClientError extends Error {
    constructor(
        readonly code: NestErrorCode,
        message: string,
        readonly data?: unknown
    ) {
        super(message)
        this.name = "NestClientError"
    }
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => globalThis.setTimeout(resolve, ms))
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function humanizeResponseSize(chars: number): string {
    if (chars < 1024) return `${chars} B`
    const units = ["KB", "MB", "GB"]
    let value = chars
    for (const unit of units) {
        value /= 1024
        if (value < 1024 || unit === units[units.length - 1]) {
            return `${value.toFixed(1)} ${unit}`
        }
    }
    return `${chars} B`
}

function isNestResponse(value: unknown): value is NestResponse {
    if (!isRecord(value)) return false
    if (typeof value.id !== "number") return false
    if (value.error !== undefined) {
        if (!isRecord(value.error)) return false
        if (typeof value.error.code !== "string" || typeof value.error.message !== "string") return false
    }
    return "result" in value || "error" in value
}

const NEST_NOTIFICATION_METHOD_SET = new Set<string>(NEST_NOTIFICATION_METHODS)
const NEST_ERROR_CODE_SET = new Set<string>(NEST_ERROR_CODES)
const DEFAULT_REQUEST_TIMEOUT_MS = 60_000
const DEFAULT_SLOW_RPC_MS = 150

function isNestNotification(value: unknown): value is NestNotification {
    return isRecord(value) && typeof value.method === "string" && NEST_NOTIFICATION_METHOD_SET.has(value.method) && !("id" in value)
}

function isClientToolCallNotification(value: unknown): value is ClientToolCallNotification {
    return (
        isRecord(value) &&
        typeof value.callId === "string" &&
        typeof value.toolsetId === "string" &&
        typeof value.sessionId === "string" &&
        typeof value.turnId === "string" &&
        (value.mode === undefined || value.mode === "full" || value.mode === "read-only") &&
        (value.permissionMode === undefined || value.permissionMode === "ask" || value.permissionMode === "auto" || value.permissionMode === "yolo") &&
        typeof value.name === "string" &&
        "arguments" in value
    )
}

function isClientToolCancelNotification(value: unknown): value is ClientToolCancelNotification {
    return isRecord(value) && typeof value.callId === "string"
}

function isPermissionRequestNotification(value: unknown): value is PermissionRequestNotification {
    return (
        isRecord(value) &&
        typeof value.requestId === "string" &&
        typeof value.workspaceId === "string" &&
        typeof value.sessionId === "string" &&
        typeof value.turnId === "string" &&
        typeof value.providerId === "string" &&
        (value.mode === "ask" || value.mode === "auto") &&
        (value.kind === "tool" || value.kind === "command" || value.kind === "file-change" || value.kind === "permissions") &&
        "input" in value
    )
}

function connectionKey(connection: NestConnection): string {
    return `${JSON.stringify(connection.routes)}|${connection.nodeId}|${connection.token}`
}

function connectionLabel(connection: NestConnection): string {
    return connection.routes.map(nestRouteLabel).join(" or ")
}

export function isNestUnauthorizedError(error: unknown): error is NestClientError {
    return error instanceof NestClientError && error.code === "unauthorized"
}

async function checkCredential(connection: NestConnection, route: NestRoute, signal: AbortSignal): Promise<void> {
    const response = await routeRequest(blindbindRoute(route), "/v1/auth", {
        method: "GET",
        headers: { Authorization: `Bearer ${connection.token}` },
        cache: "no-store",
        signal,
    })
    if (response.status === 401) {
        throw new NestClientError("unauthorized", "This device no longer has access to this computer")
    }
    if (!response.ok) throw new Error(`Nest credential check failed (${response.status})`)
}

const preferredRouteByNode = new Map<string, string>()

export function requestNestHTTP(connection: NestConnection, path: string, init: RequestInit = {}): Promise<Response> {
    const preferred = preferredRouteByNode.get(connection.nodeId)
    const route = connection.routes.find((candidate) => nestRouteKey(candidate) === preferred) ?? connection.routes[0]
    if (!route) return Promise.reject(new Error("Nest connection has no routes"))
    return routeRequest(blindbindRoute(route), path, init)
}

function subscriptionKey(subscription: NestSubscriptionFilter): string {
    return JSON.stringify(subscription)
}

function subscriptionSetKey(subscriptions: NestSubscriptionFilter[]): string {
    return subscriptions.map(subscriptionKey).sort().join("\n")
}

function uniqueSubscriptions(subscriptions: NestSubscriptionFilter[]): NestSubscriptionFilter[] {
    const seen = new Set<string>()
    return subscriptions.filter((subscription) => {
        const key = subscriptionKey(subscription)
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

function isKnownErrorCode(code: string): code is NestKnownErrorCode {
    return NEST_ERROR_CODE_SET.has(code)
}

function normalizeErrorCode(code: string, data: unknown): { code: NestErrorCode; data?: unknown } {
    if (isKnownErrorCode(code)) return { code, data }
    return {
        code: "unknown",
        data: isRecord(data) ? { ...data, rawCode: code } : { rawCode: code, data },
    }
}

export class NestClient {
    private socket: MessageSocket | null = null
    private connection: NestConnection | null = null
    private nextId = 1
    private retryMs = 250
    private stopped = false
    private suspended = false
    private connecting: Promise<void> | null = null
    private credentialCheck: AbortController | null = null
    private connectionGeneration = 0
    private preferredRouteKey: string | null = null
    private connectionFailure: NestConnectionFailure | null = null
    private lastStatus: NestClientStatus | null = null
    private lastCursor: string | null = null
    private pinnedSubscriptions: NestSubscriptionFilter[] = []
    private readonly retainedSubscriptions = new Map<string, { filter: NestSubscriptionFilter; count: number }>()
    private subscriptions: NestSubscriptionFilter[] = []
    private appliedSubscriptionKey: string | null = null
    private subscriptionUpdateQueue: Promise<void> = Promise.resolve()
    private readonly pending = new Map<NestRequestId, PendingRequest>()
    private readonly clientTools = new ClientToolRegistry()
    private readonly activeClientTools = new Map<string, { socket: MessageSocket; controller: AbortController }>()
    private permissionHandler: PermissionPromptHandler | null = null
    private readonly handlers = new Map<string, Set<NestNotificationHandler>>()
    private readonly statusHandlers = new Set<NestStatusHandler>()
    private readonly connectionFailureHandlers = new Set<NestConnectionFailureHandler>()

    constructor(private readonly onStatus?: (status: NestClientStatus) => void) {}

    async connect(connection: NestConnection): Promise<void> {
        if (this.suspended) throw new Error("Nest client is suspended while the app is backgrounded")
        if (this.stopped) this.connectionFailure = null
        if (this.connection && connectionKey(this.connection) !== connectionKey(connection)) {
            this.close()
            this.lastCursor = null
            this.preferredRouteKey = null
            this.connectionFailure = null
        }
        this.connection = connection
        if (this.socket?.readyState === 1) return
        if (this.connecting) return this.connecting

        this.stopped = false
        const generation = ++this.connectionGeneration
        const connecting = this.openLoop(generation)
        this.connecting = connecting
        try {
            await connecting
        } finally {
            if (this.connecting === connecting) this.connecting = null
        }
    }

    async requestMethod<M extends NestMethod>(method: M, ...args: NestRequestMethodArgs<M>): Promise<NestMethodResult<M>> {
        const [params, options] = args
        await this.ensureConnected()
        const socket = this.socket
        if (!socket || socket.readyState !== 1) throw new Error("Nest socket is not connected")
        return this.sendRequest<NestMethodResult<M>>(socket, method, this.clientTools.attachToTurn(method, params), options)
    }

    async requestModuleMethod<TResult>(method: string, params: unknown, options?: NestRequestOptions): Promise<TResult> {
        await this.ensureConnected()
        const socket = this.socket
        if (!socket || socket.readyState !== 1) throw new Error("Nest socket is not connected")
        return this.sendRequest<TResult>(socket, method, this.clientTools.attachToTurn(method, params), options)
    }

    registerClientTool<TSchema extends ZodTypeAny>(definition: ClientToolDefinition<TSchema>, callback: ClientToolCallback<TSchema>): () => void {
        return this.clientTools.register(definition, callback)
    }

    registerPermissionHandler(handler: PermissionPromptHandler): () => void {
        if (this.permissionHandler) throw new Error("A Nest permission handler is already registered")
        this.permissionHandler = handler
        const subscription: NestSubscriptionFilter = {
            method: "permission.request",
        }
        this.adjustRetainedSubscriptions([subscription], 1)
        if (this.socket?.readyState === 1) {
            void this.queueSubscriptionUpdate().catch((error) => console.warn("[Nest] Failed to subscribe to permission prompts", error))
        }

        let registered = true
        return () => {
            if (!registered) return
            registered = false
            if (this.permissionHandler === handler) this.permissionHandler = null
            this.adjustRetainedSubscriptions([subscription], -1)
            if (this.socket?.readyState === 1) {
                void this.queueSubscriptionUpdate().catch((error) => console.warn("[Nest] Failed to release permission prompt subscription", error))
            }
        }
    }

    async subscribe(subscriptions: NestSubscriptionFilter[]): Promise<void> {
        this.pinnedSubscriptions = uniqueSubscriptions(subscriptions)
        await this.queueSubscriptionUpdate()
    }

    async addSubscriptions(subscriptions: NestSubscriptionFilter[]): Promise<void> {
        this.pinnedSubscriptions = uniqueSubscriptions([...this.pinnedSubscriptions, ...subscriptions])
        await this.queueSubscriptionUpdate()
    }

    async retainSubscriptions(subscriptions: NestSubscriptionFilter[]): Promise<() => void> {
        const retained = uniqueSubscriptions(subscriptions)
        this.adjustRetainedSubscriptions(retained, 1)
        try {
            await this.queueSubscriptionUpdate()
        } catch (error) {
            this.adjustRetainedSubscriptions(retained, -1)
            void this.queueSubscriptionUpdate().catch(() => undefined)
            throw error
        }

        let released = false
        return () => {
            if (released) return
            released = true
            this.adjustRetainedSubscriptions(retained, -1)
            void this.queueSubscriptionUpdate().catch((error) => {
                console.warn("[Nest] Failed to release notification subscriptions", error)
            })
        }
    }

    on<M extends NestNotificationMethod>(method: M, handler: NestNotificationHandler<M>): () => void {
        const handlers = this.handlers.get(method) ?? new Set<NestNotificationHandler>()
        handlers.add(handler as NestNotificationHandler)
        this.handlers.set(method, handlers)
        return () => {
            handlers.delete(handler as NestNotificationHandler)
            if (handlers.size === 0) this.handlers.delete(method)
        }
    }

    onStatusChange(handler: NestStatusHandler): () => void {
        this.statusHandlers.add(handler)
        return () => {
            this.statusHandlers.delete(handler)
        }
    }

    onConnectionFailure(handler: NestConnectionFailureHandler): () => void {
        this.connectionFailureHandlers.add(handler)
        return () => {
            this.connectionFailureHandlers.delete(handler)
        }
    }

    getDiagnostics(): NestClientDiagnostics {
        return {
            status: this.lastStatus ?? "idle",
            nodeId: this.connection?.nodeId ?? null,
            lastCursor: this.lastCursor,
            suspended: this.suspended,
        }
    }

    getConnectionFailure(): NestConnectionFailure | null {
        return this.connectionFailure
    }

    close(): void {
        this.connectionGeneration += 1
        this.stopped = true
        this.suspended = false
        this.connecting = null
        this.credentialCheck?.abort()
        this.credentialCheck = null
        if (this.socket) this.abortClientToolsForSocket(this.socket)
        this.socket?.close()
        this.socket = null
        this.appliedSubscriptionKey = null
        for (const [id, pending] of this.pending.entries()) {
            this.rejectPending(id, pending, new Error("Nest socket closed"))
        }
        this.emitStatus("disconnected")
    }

    suspend(): void {
        if (this.suspended || this.stopped) return
        this.connectionGeneration += 1
        this.suspended = true
        this.connecting = null
        this.credentialCheck?.abort()
        this.credentialCheck = null
        if (this.socket) this.abortClientToolsForSocket(this.socket)
        this.socket?.close()
        this.socket = null
        this.appliedSubscriptionKey = null
        for (const [id, pending] of this.pending.entries()) {
            this.rejectPending(id, pending, new Error("Nest socket suspended while the app is backgrounded"))
        }
        this.emitStatus("disconnected")
    }

    async resume(connection: NestConnection | null = this.connection): Promise<void> {
        if (!connection) throw new Error("Nest connection metadata is missing")
        this.suspended = false
        await this.connect(connection)
    }

    private async ensureConnected(): Promise<void> {
        if (!this.connection) {
            throw new Error("Nest client is not connected. Call connectNestClient() during startup.")
        }
        if (this.stopped) {
            throw new Error("Nest client is closed. Call connectNestClient() before making requests.")
        }
        if (this.suspended) {
            throw new Error("Nest client is suspended while the app is backgrounded")
        }
        await this.connect(this.connection)
    }

    private async openLoop(generation: number): Promise<void> {
        while (this.isCurrentConnection(generation)) {
            this.emitStatus(this.lastStatus === "disconnected" || this.lastStatus === "reconnecting" ? "reconnecting" : "connecting")
            try {
                await this.openOnce(generation)
                this.retryMs = 250
                return
            } catch (error) {
                if (!this.isCurrentConnection(generation)) return
                if (isNestUnauthorizedError(error)) {
                    this.stopped = true
                    this.emitStatus("unauthorized")
                    throw error
                }
                if (!this.connection) throw error
                if (this.stopped) throw error
                this.emitStatus("reconnecting")
                await delay(this.retryMs)
                this.retryMs = Math.min(this.retryMs * 2, 10_000)
            }
        }
    }

    private async openOnce(generation: number): Promise<void> {
        const connection = this.connection
        if (!connection) return Promise.reject(new Error("Nest connection metadata is missing"))

        const preferred = this.preferredRouteKey ?? preferredRouteByNode.get(connection.nodeId)
        const routes = [...connection.routes].sort((left, right) => {
            if (nestRouteKey(left) === preferred) return -1
            if (nestRouteKey(right) === preferred) return 1
            return 0
        })
        if (routes.length === 0) throw new Error("Nest connection has no routes")
        let lastError: unknown
        for (let index = 0; index < routes.length; index += 1) {
            const route = routes[index]
            if (!route) continue
            try {
                await this.openRouteOnce(connection, route, generation, index < routes.length - 1 ? 1_500 : 10_000)
                const routeKey = nestRouteKey(route)
                this.preferredRouteKey = routeKey
                preferredRouteByNode.set(connection.nodeId, routeKey)
                return
            } catch (error) {
                if (isNestUnauthorizedError(error) || !this.isCurrentConnection(generation)) throw error
                const failure = classifyNestConnectionFailure(route, error)
                this.connectionFailure = failure
                for (const handler of this.connectionFailureHandlers) handler(failure)
                lastError = error
            }
        }
        throw lastError instanceof Error ? lastError : new Error("No Nest route could be reached")
    }

    private async openRouteOnce(connection: NestConnection, route: NestRoute, generation: number, handshakeTimeoutMs: number): Promise<void> {
        const credentialCheck = new AbortController()
        const credentialTimeout = globalThis.setTimeout(() => credentialCheck.abort(), handshakeTimeoutMs)
        this.credentialCheck = credentialCheck
        try {
            await checkCredential(connection, route, credentialCheck.signal)
        } finally {
            globalThis.clearTimeout(credentialTimeout)
            if (this.credentialCheck === credentialCheck) this.credentialCheck = null
        }
        if (!this.isCurrentConnection(generation)) throw new Error("Nest connection was superseded")

        const socket = await openSocket(blindbindRoute(route), "/v1/runtime", {
            protocols: [`bearer.${connection.token}`],
            handshakeTimeoutMs,
        })
        if (!this.isCurrentConnection(generation)) {
            socket.close()
            throw new Error("Nest connection was superseded")
        }
        this.socket = socket
        let initialized = false
        let rejectInterrupted: (error: Error) => void = () => undefined
        const interrupted = new Promise<never>((_resolve, reject) => {
            rejectInterrupted = reject
        })
        socket.onMessage((message) => {
            if (this.isCurrentConnection(generation)) this.handleMessage(socket, message)
        })
        socket.onError((error) => {
            if (!initialized) rejectInterrupted(error)
        })
        socket.onClose(() => {
            this.abortClientToolsForSocket(socket)
            if (this.socket === socket) this.socket = null
            this.appliedSubscriptionKey = null
            if (!this.isCurrentConnection(generation)) {
                if (!initialized) rejectInterrupted(new Error("Nest connection was superseded"))
                return
            }
            for (const [id, pending] of this.pending.entries()) {
                this.rejectPending(id, pending, new Error("Nest socket disconnected"))
            }
            if (!initialized) {
                rejectInterrupted(new Error("Nest socket closed before initialization"))
            } else if (!this.stopped) {
                this.emitStatus("reconnecting")
                globalThis.setTimeout(() => {
                    void this.ensureConnected().catch(() => undefined)
                }, 0)
            } else {
                this.emitStatus("disconnected")
            }
        })
        try {
            await Promise.race([this.initializeSocket(socket), interrupted])
            initialized = true
        } catch (error) {
            socket.close()
            if (this.socket === socket) this.socket = null
            throw error
        }
    }

    private isCurrentConnection(generation: number): boolean {
        return !this.stopped && !this.suspended && generation === this.connectionGeneration
    }

    private async initializeSocket(socket: MessageSocket): Promise<void> {
        const platform = currentPlatformKind()
        await this.sendRequest(socket, "system.initialize", {
            clientName: nestClientName(platform),
            clientPlatform: nestClientPlatform(platform),
            protocolVersion: 1,
        })
        this.subscriptions = this.desiredSubscriptions()
        if (this.subscriptions.length > 0) {
            const previousCursor = this.lastCursor
            const result = await this.sendRequest<NestMethodResult<"system.subscription.update">>(socket, "system.subscription.update", {
                subscriptions: this.subscriptions,
                ...(this.lastCursor ? { cursor: this.lastCursor } : {}),
            })
            if (this.lastCursor === previousCursor) this.lastCursor = result.cursor
        }
        this.appliedSubscriptionKey = subscriptionSetKey(this.subscriptions)
        this.connectionFailure = null
        this.emitStatus("connected")
    }

    private adjustRetainedSubscriptions(subscriptions: NestSubscriptionFilter[], delta: 1 | -1): void {
        for (const filter of subscriptions) {
            const key = subscriptionKey(filter)
            const current = this.retainedSubscriptions.get(key)
            const count = (current?.count ?? 0) + delta
            if (count <= 0) this.retainedSubscriptions.delete(key)
            else
                this.retainedSubscriptions.set(key, {
                    filter: current?.filter ?? filter,
                    count,
                })
        }
    }

    private desiredSubscriptions(): NestSubscriptionFilter[] {
        return uniqueSubscriptions([...this.pinnedSubscriptions, ...[...this.retainedSubscriptions.values()].map(({ filter }) => filter)])
    }

    private queueSubscriptionUpdate(): Promise<void> {
        const update = this.subscriptionUpdateQueue.then(() => this.updateSubscriptions())
        this.subscriptionUpdateQueue = update.catch(() => undefined)
        return update
    }

    private async updateSubscriptions(): Promise<void> {
        this.subscriptions = this.desiredSubscriptions()
        if (!this.socket || this.socket.readyState !== 1) await this.ensureConnected()

        this.subscriptions = this.desiredSubscriptions()
        const nextKey = subscriptionSetKey(this.subscriptions)
        if (this.appliedSubscriptionKey === nextKey) return

        const previousCursor = this.lastCursor
        const result = await this.requestMethod("system.subscription.update", {
            subscriptions: this.subscriptions,
            ...(this.lastCursor ? { cursor: this.lastCursor } : {}),
        })
        if (this.lastCursor === previousCursor) this.lastCursor = result.cursor
        this.appliedSubscriptionKey = nextKey
    }

    private sendRequest<TResult>(socket: MessageSocket, method: string, params?: unknown, options: NestRequestOptions = {}): Promise<TResult> {
        if (options.signal?.aborted) {
            return Promise.reject(new DOMException("Nest request aborted", "AbortError"))
        }

        const id = this.nextId++
        const message = params === undefined ? { id, method } : { id, method, params }
        const timeoutMs = options.timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS
        const slowMs = options.slowMs ?? DEFAULT_SLOW_RPC_MS

        return new Promise<TResult>((resolve, reject) => {
            const timeout = globalThis.setTimeout(() => {
                this.rejectPending(id, pending, new NestClientError("deadline_exceeded", `Nest request timed out: ${method}`))
                this.cancelRemoteRequest(socket, id)
            }, timeoutMs)

            const pending: PendingRequest = {
                method,
                params,
                startedAt: performance.now(),
                slowMs,
                resolve: (value) => resolve(value as TResult),
                reject,
                timeout,
                signal: options.signal,
            }

            const abort = () => {
                this.rejectPending(id, pending, new DOMException("Nest request aborted", "AbortError"))
                this.cancelRemoteRequest(socket, id)
            }

            pending.abort = abort
            options.signal?.addEventListener("abort", abort, { once: true })
            this.pending.set(id, pending)
            recordNestRpcLog({
                phase: "send",
                method,
                requestId: id,
                context: safeRpcLogContext(params),
                pendingCount: this.pending.size,
                socketState: socketStateName(socket.readyState),
            })

            try {
                socket.send(JSON.stringify(message))
            } catch (error) {
                this.rejectPending(id, pending, error instanceof Error ? error : new Error("Nest socket send failed"))
            }
        })
    }

    private cancelRemoteRequest(socket: MessageSocket, requestId: NestRequestId): void {
        if (socket.readyState !== 1) return
        const id = this.nextId++
        socket.send(
            JSON.stringify({
                id,
                method: "system.request.cancel",
                params: { requestId: String(requestId) },
            })
        )
    }

    private handleMessage(socket: MessageSocket, raw: string): void {
        let message: unknown
        try {
            message = JSON.parse(raw)
        } catch {
            return
        }

        if (isNestResponse(message)) {
            const pending = this.pending.get(message.id)
            if (!pending) return
            this.pending.delete(message.id)
            this.finishPending(message.id, pending, message, {
                responseChars: raw.length,
                responseSize: humanizeResponseSize(raw.length),
            })
            return
        }

        if (isNestNotification(message)) {
            if (typeof message.cursor === "string") this.lastCursor = message.cursor
            if (message.method === "client.tool.call" && isClientToolCallNotification(message.params)) {
                void this.handleClientToolCall(socket, message.params)
                return
            }
            if (message.method === "client.tool.cancel" && isClientToolCancelNotification(message.params)) {
                const active = this.activeClientTools.get(message.params.callId)
                if (active?.socket === socket) {
                    this.activeClientTools.delete(message.params.callId)
                    active.controller.abort()
                }
                return
            }
            if (message.method === "permission.request" && isPermissionRequestNotification(message.params)) {
                void this.handlePermissionRequest(socket, message.params)
                return
            }
            recordNestRpcLog({
                phase: "notification",
                method: message.method,
                cursor: typeof message.cursor === "string" ? message.cursor : undefined,
                context: safeRpcLogContext(message.params),
                pendingCount: this.pending.size,
                socketState: socketStateName(this.socket?.readyState),
            })
            const handlers = this.handlers.get(message.method)
            if (!handlers) return
            for (const handler of handlers) handler(message.params, message)
        }
    }

    private async handleClientToolCall(socket: MessageSocket, params: ClientToolCallNotification): Promise<void> {
        const previous = this.activeClientTools.get(params.callId)
        previous?.controller.abort()
        const controller = new AbortController()
        const active = { socket, controller }
        this.activeClientTools.set(params.callId, active)
        let completion: ClientToolResultParams
        try {
            completion = {
                callId: params.callId,
                result: await this.clientTools.call(params, controller.signal),
            }
        } catch (error) {
            completion = {
                callId: params.callId,
                error: {
                    code: "client_tool_error",
                    message: error instanceof Error ? error.message : "Client tool failed",
                },
            }
        } finally {
            if (this.activeClientTools.get(params.callId) === active) this.activeClientTools.delete(params.callId)
        }
        if (controller.signal.aborted) return
        try {
            await this.sendRequest(socket, "client.tool.result", completion, {
                timeoutMs: 10_000,
                slowMs: 1_000,
            })
        } catch (error) {
            console.warn("[Nest] Failed to return client tool result", {
                callId: params.callId,
                error,
            })
        }
    }

    private abortClientToolsForSocket(socket: MessageSocket): void {
        for (const [callId, active] of this.activeClientTools) {
            if (active.socket !== socket) continue
            this.activeClientTools.delete(callId)
            active.controller.abort()
        }
    }

    private async handlePermissionRequest(socket: MessageSocket, request: PermissionRequestNotification): Promise<void> {
        let response: PermissionPromptResponse
        try {
            response = this.permissionHandler
                ? await this.permissionHandler(request)
                : {
                      decision: "deny",
                      message: "No permission prompt handler is available",
                  }
        } catch (error) {
            response = {
                decision: "deny",
                message: error instanceof Error ? error.message : "Permission prompt failed",
            }
        }
        try {
            const result = await this.sendRequest<NestMethodResult<"permission.respond">>(socket, "permission.respond", {
                requestId: request.requestId,
                sessionId: request.sessionId,
                ...response,
            })
            if (!result.accepted) {
                console.warn("[Nest] Permission response arrived after the request settled", { requestId: request.requestId })
            }
        } catch (error) {
            console.warn("[Nest] Failed to return permission response", {
                requestId: request.requestId,
                error,
            })
        }
    }

    private finishPending(id: NestRequestId, pending: PendingRequest, response: NestResponse, responseSize: RpcResponseSize): void {
        this.cleanupPending(pending)
        if (response.error) {
            const normalized = normalizeErrorCode(response.error.code, response.error.data)
            const error = new NestClientError(normalized.code, response.error.message, normalized.data)
            this.logRpc(id, pending, "error", error.code, responseSize)
            pending.reject(error)
            return
        }
        this.logRpc(id, pending, "ok", undefined, responseSize)
        pending.resolve(response.result)
    }

    private rejectPending(id: NestRequestId, pending: PendingRequest, error: Error): void {
        this.pending.delete(id)
        this.cleanupPending(pending)
        this.logRpc(id, pending, "error", error instanceof NestClientError ? error.code : error.name)
        pending.reject(error)
    }

    private cleanupPending(pending: PendingRequest): void {
        globalThis.clearTimeout(pending.timeout)
        if (pending.abort) pending.signal?.removeEventListener("abort", pending.abort)
        pending.abort = undefined
        pending.signal = undefined
    }

    private logRpc(id: NestRequestId, pending: PendingRequest, status: "ok" | "error", errorCode?: string, responseSize?: RpcResponseSize): void {
        const durationMs = Math.round(performance.now() - pending.startedAt)
        const context = safeRpcLogContext(pending.params)
        const methodStats = recordNestRpcMethodDuration(pending.method, durationMs)
        recordNestRpcLog({
            phase: status,
            method: pending.method,
            requestId: id,
            durationMs,
            status,
            ...(errorCode ? { errorCode } : {}),
            context,
            pendingCount: this.pending.size,
            socketState: socketStateName(this.socket?.readyState),
            methodStats,
            ...responseSize,
        })
        if (status === "ok" && durationMs <= pending.slowMs && !isNestRpcTraceEnabled()) return
        const payload = {
            method: pending.method,
            requestId: id,
            durationMs,
            status,
            ...(errorCode ? { errorCode } : {}),
            methodStats,
            ...responseSize,
            ...context,
        }
        if (status === "ok") console.warn("[Nest] slow rpc", payload)
        else console.warn("[Nest] rpc failed", payload)
    }

    private emitStatus(status: NestClientStatus): void {
        if (this.lastStatus === status) return
        this.lastStatus = status
        recordNestRpcLog({
            phase: "status",
            status,
            pendingCount: this.pending.size,
            socketState: socketStateName(this.socket?.readyState),
        })
        this.onStatus?.(status)
        for (const handler of this.statusHandlers) handler(status)
    }
}

export function createNestClient(onStatus?: (status: NestClientStatus) => void): NestClient {
    return new NestClient(onStatus)
}

export const nestClient = createNestClient()

type NestConnectionProvider = () => Promise<NestConnection>

let configuredConnectionProvider: NestConnectionProvider | null = null

export function setNestConnectionProvider(provider: NestConnectionProvider | null): void {
    configuredConnectionProvider = provider
}

export function isNestApiAvailable(): boolean {
    return configuredConnectionProvider !== null || (typeof window !== "undefined" && typeof window.wildlarkAPI?.nest?.getConnection === "function")
}

export async function getNestConnection(): Promise<NestConnection> {
    if (!isNestApiAvailable()) throw new Error("Nest connection API is required")
    if (configuredConnectionProvider) return configuredConnectionProvider()
    const connection = await window.wildlarkAPI?.nest.getConnection()
    if (!connection) throw new Error("Nest connection metadata is required")
    return connection
}

export async function connectNestConnection(client: NestClient, connection: NestConnection, options: NestConnectOptions = {}): Promise<void> {
    if (!options.timeoutMs || options.timeoutMs <= 0) {
        await client.connect(connection)
        return
    }
    let timeout: ReturnType<typeof globalThis.setTimeout> | null = null
    try {
        await Promise.race([
            client.connect(connection),
            new Promise<never>((_resolve, reject) => {
                timeout = globalThis.setTimeout(() => reject(new Error(`Timed out connecting to ${connectionLabel(connection)}`)), options.timeoutMs)
            }),
        ])
    } catch (error) {
        client.close()
        throw error
    } finally {
        if (timeout) globalThis.clearTimeout(timeout)
    }
}

export async function connectNestClient(options: NestConnectOptions = {}): Promise<NestClient> {
    const connection = await getNestConnection()
    await connectNestConnection(nestClient, connection, options)
    return nestClient
}

export async function requestNestMethod<M extends NestMethod>(method: M, ...args: NestRequestMethodArgs<M>): Promise<NestMethodResult<M>> {
    return nestClient.requestMethod(method, ...args)
}

export function registerClientTool<TSchema extends ZodTypeAny>(definition: ClientToolDefinition<TSchema>, callback: ClientToolCallback<TSchema>): () => void {
    if (!definition.name.startsWith(WILDLARK_TOOL_PREFIX)) {
        throw new Error(`WildLark client tool names must start with ${WILDLARK_TOOL_PREFIX}`)
    }
    return nestClient.registerClientTool(definition, callback)
}

export function registerPermissionHandler(handler: PermissionPromptHandler): () => void {
    return nestClient.registerPermissionHandler(handler)
}
